import React, { HTMLInputTypeAttribute, InputHTMLAttributes } from 'react'
import {
  FieldPath,
  FieldValues,
  get,
  RegisterOptions,
  useFormContext
} from 'react-hook-form'
import { ParagraphTypes } from '../../../theme/basic/typography'
import {
  StyledDescription,
  StyledError,
  StyledTextAreaField,
  StyledFieldContainer,
  StyledLabel,
  StyledOuterLabel,
  StyledRequired
} from '../shared-style'

interface IProps<TFieldValues extends FieldValues = FieldValues>
  extends Omit<InputHTMLAttributes<HTMLTextAreaElement>, 'required' | 'name'> {
  name: FieldPath<TFieldValues>
  type?: HTMLInputTypeAttribute
  label?: string
  description?: string
  required?: boolean
  registerOptions?: RegisterOptions
  validationMessage?: string
  disabled?: boolean
  isBold?: boolean
  hasRevealButton?: boolean
  isRevealed?: boolean
  handleReveal?: (value: React.SetStateAction<boolean>) => void
}

function TextArea<TFieldValues extends FieldValues> ({
  name,
  type = 'text',
  label,
  description,
  required = false,
  registerOptions = {},
  validationMessage,
  onChange,
  disabled,
  isBold,
  hasRevealButton = false,
  isRevealed = false,
  handleReveal,
  ...attributes
}: IProps<TFieldValues>): JSX.Element {
  const {
    register,
    formState: { errors }
  } = useFormContext<TFieldValues>()
  const error = get(errors, name)

  const registerResult = register(name, {
    required,
    ...registerOptions
  })

  return (
    <StyledOuterLabel htmlFor={name}>
      {(label != null) && (
        <StyledLabel isDisabled={disabled}>
          {required && <StyledRequired>*</StyledRequired>}
          {label}
          {(description != null) && <StyledDescription>{description}</StyledDescription>}
        </StyledLabel>
      )}
      <StyledFieldContainer>
        <StyledTextAreaField
          id={name}
          aria-describedby={name}
          required={required}
          aria-required={required}
          hasError={error}
          disabled={disabled}
          isBold={isBold}
          rows={3}
          {...attributes}
          {...registerResult}
          onChange={(event) => {
            void registerResult.onChange(event)
            onChange?.(event)
          }}
        />
      </StyledFieldContainer>

      <StyledError type={ParagraphTypes.PARAGRAPH_HIGHLIGHT}>
          {error?.message}
        </StyledError>

      {(Boolean(error)) && (validationMessage != null) && (
        <StyledError type={ParagraphTypes.PARAGRAPH_HIGHLIGHT}>
          {validationMessage}
        </StyledError>
      )}
    </StyledOuterLabel>
  )
}

export default TextArea
